/**
 * 기관코드별 사이드바 로고
 * 원본은 크게 받아도, 저장은 로고 영역(펼침 168×28 CSS, 레티나 3배)에 맞춰 축소한다.
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList } from 'services/commonQuery';
import { enqueuePersist } from 'services/pocRowPersist';

const LOGO_SAVE_QUERY = ['CmTenantLogo.insertLogo', 'CmTenantLogo.updateLogo', 'CmTenantLogo.deleteLogo'];

export const TENANT_LOGO_STORAGE_KEY = 'erp.tenantLogos';
export const TENANT_LOGO_CHANGED_EVENT = 'erp-tenant-logo-changed';

/** 원본 파일 상한 — 저장본이 아니라 브라우저 디코드 부담 가드 */
export const TENANT_LOGO_INPUT_MAX_BYTES = 20 * 1024 * 1024;
/** 한 변 픽셀 상한. 넘으면 디코드 단계에서 먼저 줄인다 */
export const TENANT_LOGO_MAX_EDGE_PX = 8000;
/** 사이드바 표시(168×28) × 3 — 업스케일 없음 */
export const TENANT_LOGO_TARGET_W = 168 * 3;
export const TENANT_LOGO_TARGET_H = 28 * 3;
/** SVG는 래스터화하지 않음. 이 용량 이하면 원본 유지 */
export const TENANT_LOGO_SVG_MAX_BYTES = 200 * 1024;
/** 리사이즈 후 dataURL 안전 상한 (localStorage·PoC JSON) */
export const TENANT_LOGO_STORE_MAX_BYTES = 450 * 1024;
/** @deprecated 저장 상한. STORE_MAX 와 동일 */
export const TENANT_LOGO_MAX_BYTES = TENANT_LOGO_STORE_MAX_BYTES;
/** 여백 분석용 축소본의 긴 변 (원본 전체를 스캔하지 않는다) */
export const TENANT_LOGO_TRIM_ANALYZE_EDGE = 512;
/** 배경색과 이 거리(RGB 유클리드) 이내면 배경으로 본다. JPG 노이즈 흡수용 */
export const TENANT_LOGO_TRIM_TOLERANCE = 24;
/** 저장 이미지 높이가 표시 높이의 이 배수 미만이면 저해상도 경고 */
export const TENANT_LOGO_LOW_RES_RATIO = 1.5;
const LOGO_DISPLAY_H = 28;
const LOGO_DISPLAY_W = 168;

/**
 * @typedef {{
 *   logoDataUrl: string,
 *   logoDarkDataUrl?: string,
 *   logoAlt?: string,
 *   logoAreaDark?: boolean,
 *   updatedAt?: string,
 * }} TenantLogoEntry
 */

/**
 * @typedef {{
 *   dataUrl: string,
 *   originalBytes: number,
 *   storedBytes: number,
 *   width: number,
 *   height: number,
 *   resized: boolean,
 *   format: 'png' | 'svg',
 *   trimmed: boolean,
 *   trimNote?: string,
 *   lowRes?: boolean,
 * }} TenantLogoPrepareResult
 */

function normalizeCode(compCd) {
  return String(compCd || '').trim().toUpperCase();
}

export function formatLogoBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v}B`;
  if (v < 1024 * 1024) {
    const kb = v / 1024;
    return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)}KB`;
  }
  if (v % (1024 * 1024) === 0) return `${v / (1024 * 1024)}MB`;
  return `${(v / (1024 * 1024)).toFixed(1)}MB`;
}

/** @returns {Record<string, TenantLogoEntry>} */
export function loadAllTenantLogos() {
  try {
    const raw = localStorage.getItem(TENANT_LOGO_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

/** @param {string} compCd */
export function loadTenantLogo(compCd) {
  const code = normalizeCode(compCd);
  if (!code) return null;
  const entry = loadAllTenantLogos()[code];
  if (!entry?.logoDataUrl) return null;
  return { ...entry, tenantCode: code };
}

/**
 * API에서 로고를 읽어 로컬 캐시(localStorage)를 갱신한다. 갱신되면 TENANT_LOGO_CHANGED_EVENT 를 쏜다.
 * @param {string} compCd
 * @returns {Promise<TenantLogoEntry|null>}
 */
export async function fetchTenantLogo(compCd) {
  const code = normalizeCode(compCd);
  if (!code || !isBdApiEnabled()) return loadTenantLogo(code);
  const rows = await commonSelect('CmTenantLogo.selectLogo', { tenantCode: code });
  const row = rows?.[0];
  if (!row?.logoDataUrl) return loadTenantLogo(code);
  const all = loadAllTenantLogos();
  all[code] = {
    logoDataUrl: row.logoDataUrl,
    ...(row.logoDarkDataUrl ? { logoDarkDataUrl: row.logoDarkDataUrl } : {}),
    logoAlt: row.logoAlt || code,
    logoAreaDark: row.logoAreaDark === 'Y',
    updatedAt: new Date().toISOString(),
  };
  saveAllTenantLogos(all);
  return loadTenantLogo(code);
}

/**
 * @param {Record<string, TenantLogoEntry>} map
 */
export function saveAllTenantLogos(map) {
  const next = map && typeof map === 'object' ? map : {};
  localStorage.setItem(TENANT_LOGO_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TENANT_LOGO_CHANGED_EVENT));
  }
  return next;
}

/**
 * @param {string} compCd
 * @param {{ logoDataUrl: string, logoDarkDataUrl?: string, logoAlt?: string, logoAreaDark?: boolean }} payload
 *   logoDarkDataUrl 이 비어 있으면 다크 로고는 저장하지 않는다(기존 값도 제거).
 */
export function saveTenantLogo(compCd, payload) {
  const code = normalizeCode(compCd);
  if (!code) return { ok: false, message: '기관코드를 입력하세요.' };
  const dataUrl = String(payload?.logoDataUrl || '');
  if (!dataUrl.startsWith('data:image/')) {
    return { ok: false, message: '이미지 파일이 필요합니다.' };
  }
  if (dataUrl.length > TENANT_LOGO_STORE_MAX_BYTES) {
    return { ok: false, message: '로고 영역에 맞춰 줄였지만 저장 용량을 초과했습니다.' };
  }
  const darkDataUrl = String(payload?.logoDarkDataUrl || '');
  if (darkDataUrl) {
    if (!darkDataUrl.startsWith('data:image/')) {
      return { ok: false, message: '다크 로고는 이미지 파일이어야 합니다.' };
    }
    if (darkDataUrl.length > TENANT_LOGO_STORE_MAX_BYTES) {
      return { ok: false, message: '다크 로고가 저장 용량을 초과했습니다.' };
    }
  }
  const all = loadAllTenantLogos();
  all[code] = {
    logoDataUrl: dataUrl,
    ...(darkDataUrl ? { logoDarkDataUrl: darkDataUrl } : {}),
    logoAlt: String(payload?.logoAlt || '').trim() || code,
    logoAreaDark: payload?.logoAreaDark === true,
    updatedAt: new Date().toISOString(),
  };
  try {
    saveAllTenantLogos(all);
  } catch {
    return { ok: false, message: '브라우저 저장 공간이 부족합니다.' };
  }
  if (isBdApiEnabled()) {
    const entry = all[code];
    enqueuePersist(`logo|${code}`, async () => {
      const rows = await commonSelect('CmTenantLogo.selectLogo', { tenantCode: code });
      await commonSaveList(
        LOGO_SAVE_QUERY,
        [
          {
            logoDataUrl: entry.logoDataUrl,
            logoDarkDataUrl: entry.logoDarkDataUrl || null,
            logoAlt: entry.logoAlt,
            logoAreaDark: entry.logoAreaDark ? 'Y' : 'N',
            mod: rows.length ? 'U' : 'A',
          },
        ],
        { tenantCode: code }
      );
    });
  }
  return { ok: true, entry: all[code] };
}

/** @param {string} compCd */
export function removeTenantLogo(compCd) {
  const code = normalizeCode(compCd);
  if (!code) return { ok: false, message: '기관코드를 입력하세요.' };
  const all = loadAllTenantLogos();
  if (!all[code]) return { ok: true };
  delete all[code];
  saveAllTenantLogos(all);
  if (isBdApiEnabled()) {
    enqueuePersist(`logo|${code}`, () =>
      commonSaveList(LOGO_SAVE_QUERY, [{ mod: 'D' }], { tenantCode: code })
    );
  }
  return { ok: true };
}

function isImageFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|svg)$/.test(name);
}

function isSvgFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type === 'image/svg+xml' || name.endsWith('.svg');
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });
}

function fitContain(srcW, srcH, maxW, maxH) {
  const scale = Math.min(maxW / srcW, maxH / srcH, 1);
  return {
    w: Math.max(1, Math.round(srcW * scale)),
    h: Math.max(1, Math.round(srcH * scale)),
    scale,
  };
}

function closeBitmap(bmp) {
  if (bmp && typeof bmp.close === 'function') bmp.close();
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지를 읽지 못했습니다.'));
    };
    img.src = url;
  });
}

async function decodeToBitmap(file) {
  const baseOpts = { imageOrientation: 'from-image', resizeQuality: 'high' };
  let bmp = null;
  if (typeof createImageBitmap === 'function') {
    try {
      bmp = await createImageBitmap(file, baseOpts);
    } catch {
      bmp = null;
    }
  }
  if (!bmp) {
    const img = await loadImageElement(file);
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (Math.max(iw, ih) > TENANT_LOGO_MAX_EDGE_PX) {
      throw new Error(`이미지가 너무 큽니다. 한 변 ${TENANT_LOGO_MAX_EDGE_PX}px 이하로 올려 주세요.`);
    }
    return { source: img, width: iw, height: ih };
  }

  const w = bmp.width;
  const h = bmp.height;
  const long = Math.max(w, h);
  if (long > TENANT_LOGO_MAX_EDGE_PX) {
    const scale = TENANT_LOGO_MAX_EDGE_PX / long;
    const rw = Math.max(1, Math.round(w * scale));
    const rh = Math.max(1, Math.round(h * scale));
    closeBitmap(bmp);
    try {
      bmp = await createImageBitmap(file, {
        ...baseOpts,
        resizeWidth: rw,
        resizeHeight: rh,
      });
    } catch {
      throw new Error(`이미지가 너무 큽니다. 한 변 ${TENANT_LOGO_MAX_EDGE_PX}px 이하로 올려 주세요.`);
    }
  }
  return { source: bmp, width: bmp.width, height: bmp.height };
}

/**
 * @param {CanvasImageSource} source
 * @param {number} w
 * @param {number} h
 * @param {{ sx: number, sy: number, sw: number, sh: number } | null} [crop] 원본에서 잘라낼 영역
 */
function drawToPngDataUrl(source, w, h, crop) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('이미지를 줄이지 못했습니다.');
  ctx.clearRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (crop) {
    ctx.drawImage(source, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, w, h);
  } else {
    ctx.drawImage(source, 0, 0, w, h);
  }
  return canvas.toDataURL('image/png');
}

function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

/**
 * 로고 내용 영역(투명/단색 여백 제외)을 찾는다.
 * 분석은 긴 변 TRIM_ANALYZE_EDGE 이하의 축소본에서만 하고, 결과는 원본 좌표로 환산한다.
 * @returns {{ crop: { sx: number, sy: number, sw: number, sh: number } | null, skip?: 'nonsolid'|'blank'|'noMargin'|'error' }}
 */
function detectContentBounds(source, srcW, srcH) {
  try {
    const scale = Math.min(1, TENANT_LOGO_TRIM_ANALYZE_EDGE / Math.max(srcW, srcH));
    const aw = Math.max(1, Math.round(srcW * scale));
    const ah = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = aw;
    canvas.height = ah;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { crop: null, skip: 'error' };
    ctx.drawImage(source, 0, 0, aw, ah);
    const { data } = ctx.getImageData(0, 0, aw, ah);

    const at = (x, y) => (y * aw + x) * 4;
    const corners = [at(0, 0), at(aw - 1, 0), at(0, ah - 1), at(aw - 1, ah - 1)];
    const transparentBg = corners.every((i) => data[i + 3] <= 8);

    let isContent;
    if (transparentBg) {
      isContent = (i) => data[i + 3] > 8;
    } else {
      if (corners.some((i) => data[i + 3] < 200)) return { crop: null, skip: 'nonsolid' };
      const bg = [0, 1, 2].map((k) => corners.reduce((sum, i) => sum + data[i + k], 0) / corners.length);
      const tol = TENANT_LOGO_TRIM_TOLERANCE;
      if (corners.some((i) => colorDistance(data[i], data[i + 1], data[i + 2], bg[0], bg[1], bg[2]) > tol)) {
        return { crop: null, skip: 'nonsolid' };
      }
      isContent = (i) =>
        data[i + 3] > 8 && colorDistance(data[i], data[i + 1], data[i + 2], bg[0], bg[1], bg[2]) > tol;
    }

    let minX = aw;
    let minY = ah;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < ah; y += 1) {
      for (let x = 0; x < aw; x += 1) {
        if (isContent(at(x, y))) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return { crop: null, skip: 'blank' };

    // 분석 좌표 → 원본 좌표. 반올림 오차·안티앨리어싱은 패딩으로 흡수
    const pad = Math.max(2, Math.round(Math.max(srcW, srcH) * 0.02));
    const sx = Math.max(0, Math.floor(minX / scale) - pad);
    const sy = Math.max(0, Math.floor(minY / scale) - pad);
    const ex = Math.min(srcW, Math.ceil((maxX + 1) / scale) + pad);
    const ey = Math.min(srcH, Math.ceil((maxY + 1) / scale) + pad);
    const sw = ex - sx;
    const sh = ey - sy;

    if (sw < 8 || sh < 8) return { crop: null, skip: 'blank' };
    if (sw * sh >= srcW * srcH * 0.95) return { crop: null, skip: 'noMargin' };
    return { crop: { sx, sy, sw, sh } };
  } catch {
    // 분석 실패해도 업로드는 기존 경로로 진행
    return { crop: null, skip: 'error' };
  }
}

/** 사이드바 표시 크기(높이 28 / 가로 168 이내) 대비 저장 이미지가 너무 작은지 */
function isLowResolution(w, h) {
  const displayH = Math.min(LOGO_DISPLAY_H, (LOGO_DISPLAY_W * h) / w);
  return h < displayH * TENANT_LOGO_LOW_RES_RATIO;
}

/**
 * 원본 파일을 사이드바 로고 영역에 맞춰 PNG(또는 소용량 SVG) dataURL로 만든다.
 * @param {File} file
 * @param {{ autoTrim?: boolean }} [options] autoTrim: 투명/단색 여백 자동 제거 (래스터만, 기본 true)
 * @returns {Promise<TenantLogoPrepareResult>}
 */
export async function prepareTenantLogoFromFile(file, { autoTrim = true } = {}) {
  if (!file || !isImageFile(file)) {
    throw new Error('이미지 파일만 업로드할 수 있습니다.');
  }
  if (file.size > TENANT_LOGO_INPUT_MAX_BYTES) {
    throw new Error(`원본은 ${formatLogoBytes(TENANT_LOGO_INPUT_MAX_BYTES)}까지 올릴 수 있습니다.`);
  }

  if (isSvgFile(file)) {
    if (file.size > TENANT_LOGO_SVG_MAX_BYTES) {
      throw new Error(
        `SVG는 벡터로 유지합니다. ${formatLogoBytes(TENANT_LOGO_SVG_MAX_BYTES)} 이하로 올려 주세요.`
      );
    }
    const dataUrl = await readFileAsDataUrl(file);
    if (dataUrl.length > TENANT_LOGO_STORE_MAX_BYTES) {
      throw new Error('SVG 용량이 저장 한도를 초과합니다.');
    }
    return {
      dataUrl,
      originalBytes: file.size,
      storedBytes: dataUrl.length,
      width: 0,
      height: 0,
      resized: false,
      format: 'svg',
      trimmed: false,
    };
  }

  const decoded = await decodeToBitmap(file);
  try {
    if (decoded.width < 1 || decoded.height < 1) {
      throw new Error('이미지를 읽지 못했습니다.');
    }
    let crop = null;
    let trimNote = '';
    if (autoTrim) {
      const det = detectContentBounds(decoded.source, decoded.width, decoded.height);
      if (det.crop) {
        crop = det.crop;
        trimNote = `여백 제거 (원본 ${decoded.width}×${decoded.height} → ${crop.sw}×${crop.sh})`;
      } else if (det.skip === 'nonsolid') {
        trimNote = '배경이 단색이 아니라 여백 제거를 건너뛰었습니다.';
      }
    }
    const baseW = crop ? crop.sw : decoded.width;
    const baseH = crop ? crop.sh : decoded.height;
    let { w, h, scale } = fitContain(baseW, baseH, TENANT_LOGO_TARGET_W, TENANT_LOGO_TARGET_H);
    let dataUrl = drawToPngDataUrl(decoded.source, w, h, crop);
    let guard = 0;
    while (dataUrl.length > TENANT_LOGO_STORE_MAX_BYTES && w > 80 && h > 14 && guard < 8) {
      w = Math.max(80, Math.round(w * 0.82));
      h = Math.max(14, Math.round(h * 0.82));
      dataUrl = drawToPngDataUrl(decoded.source, w, h, crop);
      guard += 1;
    }
    if (dataUrl.length > TENANT_LOGO_STORE_MAX_BYTES) {
      throw new Error('로고 영역에 맞춰 줄였지만 저장 용량을 초과했습니다.');
    }
    return {
      dataUrl,
      originalBytes: file.size,
      storedBytes: dataUrl.length,
      width: w,
      height: h,
      resized: scale < 0.999 || file.size > dataUrl.length,
      format: 'png',
      trimmed: !!crop,
      trimNote,
      lowRes: isLowResolution(w, h),
    };
  } finally {
    closeBitmap(decoded.source);
  }
}

/**
 * File → 로고용 dataURL (축소 포함)
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function readImageFileAsDataUrl(file) {
  const prepared = await prepareTenantLogoFromFile(file);
  return prepared.dataUrl;
}
